import { useState } from 'react';
import type { ImportedItem } from '@heybesafe/shared';
import { suggestDomainForName, fillMissingUrlsFromNames } from '@heybesafe/shared';
import { EyeToggle } from './hbs/EyeToggle';

interface BulkEntryTableProps {
  /** One ImportedItem per non-blank row; Site→url; empties→undefined; run
   *  through fillMissingUrlsFromNames before emitting. */
  onSave: (items: ImportedItem[]) => void;
  saving: boolean;
  onBack: () => void;
}

interface DraftRow {
  name: string;
  username: string;
  password: string;
  site: string;
  revealed: boolean;
}

const INITIAL_ROWS = 10;

function blankRow(): DraftRow {
  return { name: '', username: '', password: '', site: '', revealed: false };
}

const headerCls = 'px-3 py-2 text-left text-xs font-medium text-hbs-text-mute';
const inputCls =
  'w-full bg-transparent text-sm text-hbs-text focus:outline-none focus:ring-1 focus:ring-hbs-primary rounded px-1 py-0.5 min-w-[80px]';

export default function BulkEntryTable({ onSave, saving, onBack }: BulkEntryTableProps): JSX.Element {
  const [rows, setRows] = useState<DraftRow[]>(() =>
    Array.from({ length: INITIAL_ROWS }, blankRow),
  );
  const [focusedPw, setFocusedPw] = useState<number | null>(null);

  function patch(i: number, fields: Partial<DraftRow>) {
    setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, ...fields } : r)));
  }

  function addRow() {
    setRows(prev => [...prev, blankRow()]);
  }

  // On Name blur, fill an empty Site from the on-device brand→domain lookup.
  // Never overwrite a Site the user already typed.
  function handleNameBlur(i: number) {
    setRows(prev =>
      prev.map((r, idx) => {
        if (idx !== i) return r;
        if (r.site.trim()) return r;
        return { ...r, site: suggestDomainForName(r.name) ?? '' };
      }),
    );
  }

  function handleSave() {
    const candidates: ImportedItem[] = rows
      .filter(r => r.name.trim() || r.username.trim() || r.password.trim() || r.site.trim())
      .map(r => ({
        type: 'login' as const,
        // '' rather than undefined: `name` is required on VaultItem, and every
        // downstream consumer tests it for truthiness (fillMissingUrlsFromNames
        // does `item.name ? … : null`), so the two behave identically.
        name: r.name.trim(),
        username: r.username.trim() || undefined,
        password: r.password.trim() ? r.password : undefined,
        url: r.site.trim() || undefined,
        sensitive: false,
        favorite: false,
        tags: [],
      }));
    onSave(fillMissingUrlsFromNames(candidates));
  }

  return (
    <div className="flex flex-col gap-4">
      <button
          id="bulk-entry-back-button"
          onClick={onBack}
        className="self-start text-xs text-hbs-text-mute hover:text-hbs-text flex items-center gap-1"
      >
        ← Back
      </button>

      <p className="text-sm text-hbs-text-mute">
        Type your logins below, then save them all at once. Leave rows blank if you don't need them.
      </p>

      <div className="border border-hbs-border rounded-hbs-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-hbs-surface border-b border-hbs-border">
            <tr>
              <th scope="col" className={headerCls}>Name</th>
              <th scope="col" className={headerCls}>Username</th>
              <th scope="col" className={headerCls}>Password</th>
              <th scope="col" className={headerCls}>Site</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const pwVisible = focusedPw === i || row.revealed;
              return (
                <tr key={i} className="border-b border-hbs-border last:border-0">
                  <td className="px-2 py-1">
                    <input
                      aria-label={`Name row ${i + 1}`}
                      className={inputCls}
                      value={row.name}
                      onChange={e => patch(i, { name: e.target.value })}
                      onBlur={() => handleNameBlur(i)}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      aria-label={`Username row ${i + 1}`}
                      className={inputCls}
                      value={row.username}
                      onChange={e => patch(i, { username: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <input
                        aria-label={`Password row ${i + 1}`}
                        type={pwVisible ? 'text' : 'password'}
                        className={inputCls}
                        value={row.password}
                        onChange={e => patch(i, { password: e.target.value })}
                        onFocus={() => setFocusedPw(i)}
                        onBlur={() => setFocusedPw(prev => (prev === i ? null : prev))}
                      />
                      <EyeToggle
                        revealed={row.revealed}
                        onChange={revealed => patch(i, { revealed })}
                        label={`row ${i + 1}`}
                      />
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <input
                      aria-label={`Site row ${i + 1}`}
                      className={inputCls}
                      value={row.site}
                      onChange={e => patch(i, { site: e.target.value })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button id="bulk-entry-add-row-button"
          onClick={addRow} className="text-xs text-hbs-primary hover:text-hbs-primary">
          + Add row
        </button>
        <button
          id="bulk-entry-save-button"
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-hbs-primary hover:bg-hbs-primary-hover disabled:opacity-50 text-white text-sm font-medium rounded-hbs-md transition-colors"
        >
          {saving ? 'Saving…' : 'Save all'}
        </button>
      </div>
    </div>
  );
}
